import { AppShell, appShellLoader } from "@/components/AppShell";
import { requireGuest } from "@/lib/auth";
import { AcceptInviteRoute, acceptInviteLoader } from "@/routes/accept-invite";
import { ApproveImpersonationRoute } from "@/routes/approve-impersonation";
import {
  CliApproveRoute,
  cliApproveLoader,
  cliApproveShouldRevalidate,
} from "@/routes/cli-approve";
import { DashboardRoute } from "@/routes/dashboard";
import { LoginRoute } from "@/routes/login";
import { MembersRoute } from "@/routes/members";
import { PlaceholderRoute } from "@/routes/placeholder";
import { ProjectDetailRoute } from "@/routes/project-detail";
import { ProjectsRoute } from "@/routes/projects";
import { ResetMfaRoute } from "@/routes/reset-mfa";
import { SecretDetailRoute } from "@/routes/secret-detail";
import { SecretsRoute } from "@/routes/secrets";
import { SecretsImportRoute } from "@/routes/secrets-import";
import { SecretsNewRoute } from "@/routes/secrets-new";
import {
  SettingsAuditRoute,
  SettingsBillingRoute,
  SettingsDangerRoute,
  SettingsLayout,
  SettingsOrganizationRoute,
  SettingsProfileRoute,
  SettingsSecurityRoute,
} from "@/routes/settings";
import { SettingsServiceTokensRoute } from "@/routes/settings-service-tokens";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Navigate, RouterProvider, createBrowserRouter } from "react-router";
import "@/styles/globals.css";
import "@/styles/console.css";
import "@/styles/settings.css";
import "@/styles/members.css";

const router = createBrowserRouter([
  {
    path: "/login",
    loader: requireGuest,
    element: <LoginRoute />,
  },
  {
    path: "/cli/approve",
    loader: cliApproveLoader,
    shouldRevalidate: cliApproveShouldRevalidate,
    element: <CliApproveRoute />,
  },
  {
    path: "/accept-invite",
    loader: acceptInviteLoader,
    element: <AcceptInviteRoute />,
  },
  {
    path: "/approve-impersonation",
    element: <ApproveImpersonationRoute />,
  },
  {
    path: "/reset-mfa",
    element: <ResetMfaRoute />,
  },
  {
    id: "appshell",
    loader: appShellLoader,
    element: <AppShell />,
    children: [
      { index: true, element: <DashboardRoute /> },
      { path: "projects", element: <ProjectsRoute /> },
      { path: "projects/:projectSlug", element: <ProjectDetailRoute /> },
      {
        path: "projects/:projectSlug/envs/:envSlug/secrets",
        element: <SecretsRoute />,
      },
      {
        path: "projects/:projectSlug/envs/:envSlug/secrets/new",
        element: <SecretsNewRoute />,
      },
      {
        path: "projects/:projectSlug/envs/:envSlug/secrets/import",
        element: <SecretsImportRoute />,
      },
      {
        path: "projects/:projectSlug/envs/:envSlug/secrets/*",
        element: <SecretDetailRoute />,
      },
      // Legacy /secrets URLs predate the projects-first navigation. Bounce
      // them to the projects index so old bookmarks / external links still
      // land somewhere sensible.
      { path: "secrets", element: <Navigate to="/projects" replace /> },
      { path: "secrets/*", element: <Navigate to="/projects" replace /> },
      {
        path: "rotators",
        element: (
          <PlaceholderRoute
            eyebrow="rotators"
            title="Rotators"
            body="Schedule rotations for your API keys, database passwords, and signing secrets. Wardkey runs the rotation and updates consumers automatically."
          />
        ),
      },
      { path: "members", element: <MembersRoute /> },
      {
        path: "settings",
        element: <SettingsLayout />,
        children: [
          { index: true, element: <SettingsProfileRoute /> },
          { path: "organization", element: <SettingsOrganizationRoute /> },
          { path: "billing", element: <SettingsBillingRoute /> },
          { path: "security", element: <SettingsSecurityRoute /> },
          { path: "service-tokens", element: <SettingsServiceTokensRoute /> },
          { path: "audit", element: <SettingsAuditRoute /> },
          { path: "danger", element: <SettingsDangerRoute /> },
        ],
      },
    ],
  },
]);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing #root element");

createRoot(rootEl).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
